import { addDays, differenceInMinutes } from "date-fns";
import { Event } from "../../types/events";
import { TimelineResourceRow } from "./timelineRows";
import {
  TIMELINE_EVENT_MIN_HEIGHT,
  TIMELINE_EVENT_MIN_WIDTH,
  TIMELINE_LANE_HEIGHT_PX,
  TIMELINE_LANE_VERTICAL_PADDING_PX,
  TIMELINE_MINUTES_IN_DAY,
} from "./timelineConstants";

export interface TimelineEventPositionArgs {
  eventItem: Event;
  resourceRow: TimelineResourceRow;
  startDate: Date;
  daysToShow: number;
  zoom: number;
  baseDayWidth: number;
  laneIndex?: number;
  laneCount?: number;
  isDraggable?: boolean;
}

export interface TimelineEventPosition {
  eventStart: Date;
  eventEnd: Date;
  continuesBeforeView: boolean;
  continuesAfterView: boolean;
  canDrag: boolean;
  left: number;
  width: number;
  top: number;
  height: number;
}

export const getTimelineEventPosition = ({
  eventItem,
  resourceRow,
  startDate,
  daysToShow,
  zoom,
  baseDayWidth,
  laneIndex = 0,
  laneCount = 1,
  isDraggable = false,
}: TimelineEventPositionArgs): TimelineEventPosition => {
  const eventStart = new Date(eventItem.timeRange.start);
  const eventEnd = new Date(eventItem.timeRange.end);
  const viewStart = startDate;
  const viewEndExclusive = addDays(startDate, daysToShow);
  const continuesBeforeView = eventStart < viewStart;
  const continuesAfterView = eventEnd > viewEndExclusive;
  const canDrag = isDraggable && !continuesBeforeView && !continuesAfterView;

  const displayStart = continuesBeforeView ? viewStart : eventStart;
  const displayEnd = continuesAfterView ? viewEndExclusive : eventEnd;
  const minutesFromViewStart = differenceInMinutes(displayStart, startDate);
  const displayedMinutes = Math.max(
    0,
    differenceInMinutes(displayEnd, displayStart)
  );

  const left = (minutesFromViewStart / TIMELINE_MINUTES_IN_DAY) * baseDayWidth * zoom;
  const calculatedWidth =
    (displayedMinutes / TIMELINE_MINUTES_IN_DAY) * baseDayWidth * zoom;
  const width = Math.max(
    TIMELINE_EVENT_MIN_WIDTH,
    calculatedWidth > 0 ? calculatedWidth : TIMELINE_EVENT_MIN_WIDTH
  );

  // Fixed per-lane height keeps every event readable regardless of how many
  // overlap on this resource. When the row is taller than the lanes need (for
  // example, a single-lane row at the historical row-height floor), center the
  // lane stack so the bar does not hug the top edge.
  const safeLaneCount = Math.max(1, laneCount);
  const laneHeight = TIMELINE_LANE_HEIGHT_PX;
  const lanesTotalHeight = safeLaneCount * laneHeight;
  const innerHeight = Math.max(
    0,
    resourceRow.height - 2 * TIMELINE_LANE_VERTICAL_PADDING_PX
  );
  const extraSpace = Math.max(0, innerHeight - lanesTotalHeight);
  const topOffset =
    TIMELINE_LANE_VERTICAL_PADDING_PX + Math.floor(extraSpace / 2);
  const top = resourceRow.top + topOffset + laneIndex * laneHeight;
  const height = Math.max(TIMELINE_EVENT_MIN_HEIGHT, laneHeight - 2);

  return {
    eventStart,
    eventEnd,
    continuesBeforeView,
    continuesAfterView,
    canDrag,
    left,
    width,
    top,
    height,
  };
};
